import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPurchaseOrderById, receivePurchaseOrder, cancelPurchaseOrder } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-600",
  received: "bg-green-50 text-green-600",
  cancelled: "bg-red-50 text-red-500",
};

// A PurchaseOrder is never edited once created (see models.PurchaseOrder's
// own doc comment) — this page only ever displays it and, while still
// Pending, offers exactly the two actions that end its lifecycle: Receive
// Stock (writes the actual StockMovement/StockLevel changes) or Cancel
// (abandons it with no stock effect at all).
export default function ViewPurchaseOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("purchase_orders");
  const { t: tCommon } = useTranslation("common");

  const [order, setOrder] = useState(null);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [status, setStatus] = useState("idle");

  const load = () => {
    getPurchaseOrderById(token, id).then(setOrder).catch(() => notify.error(t("notify_load_failed", "Failed to load purchase order")));
  };

  useEffect(() => { load(); }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReceive = async () => {
    try {
      setStatus("loading");
      await receivePurchaseOrder(token, id);
      setStatus("success");
      setTimeout(() => { setReceiveModalOpen(false); setStatus("idle"); }, 1200);
      notify.success(t("notify_received", "Purchase order received — stock has been updated"));
      load();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_receive_failed", "Failed to receive purchase order"));
    }
  };

  const handleCancel = async () => {
    try {
      setStatus("loading");
      await cancelPurchaseOrder(token, id);
      setStatus("success");
      setTimeout(() => { setCancelModalOpen(false); setStatus("idle"); }, 1200);
      notify.success(t("notify_cancelled", "Purchase order cancelled"));
      load();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_cancel_failed", "Failed to cancel purchase order"));
    }
  };

  if (!order) return null;

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <ConfirmModal
        isOpen={receiveModalOpen}
        title={t("receive_confirm_title", "Receive Purchase Order")}
        message={t("receive_confirm_message", "This adds every line item's quantity to stock at this branch and can't be undone. Continue?")}
        onConfirm={handleReceive}
        onCancel={() => setReceiveModalOpen(false)}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
        confirmLabel={t("btn_receive", "Receive Stock")}
        confirmVariant="primary"
        successTitle={t("receive_success_title", "Received!")}
        successMessage={t("notify_received", "Purchase order received — stock has been updated")}
      />
      <ConfirmModal
        isOpen={cancelModalOpen}
        title={t("cancel_confirm_title", "Cancel Purchase Order")}
        message={t("cancel_confirm_message", "Are you sure? No stock has been received yet — this order will be marked cancelled.")}
        onConfirm={handleCancel}
        onCancel={() => setCancelModalOpen(false)}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
        confirmLabel={t("btn_cancel_order", "Cancel Order")}
        successTitle={t("cancel_success_title", "Cancelled!")}
        successMessage={t("notify_cancelled", "Purchase order cancelled")}
      />

      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/purchase-orders")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div className="flex-grow">
          <h2 className="text-2xl font-black text-gray-800 tracking-tight font-mono">{order.code}</h2>
          <span className={`inline-block mt-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLES[order.status] || "bg-gray-100 text-gray-500"}`}>
            {t(`status_${order.status}`, order.status)}
          </span>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_supplier_label", "Supplier")}</p>
            <p className="font-bold text-gray-800">{order.supplier?.name || `#${order.supplier_id}`}</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_branch_label", "Branch")}</p>
            <p className="font-bold text-gray-800">{order.branch?.name || `#${order.branch_id}`}</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_created_by_label", "Created By")}</p>
            <p className="font-bold text-gray-800">{order.created_by?.username || `#${order.created_by_id}`}</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_created_at_label", "Created At")}</p>
            <p className="font-bold text-gray-800">{new Date(order.created_at).toLocaleString()}</p>
          </div>
        </div>

        {order.notes && (
          <div>
            <p className="text-gray-400 text-xs font-bold uppercase mb-1">{t("detail_notes_label", "Notes")}</p>
            <p className="text-gray-700 text-sm">{order.notes}</p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3">{t("items_section_title", "Line Items")}</h3>
          <div className="space-y-2">
            {(order.items || []).map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex-grow">
                  <p className="font-bold text-gray-800 text-sm">{item.product_name}</p>
                  <p className="text-xs text-gray-400 font-mono">{item.product_sku}</p>
                </div>
                <div className="text-xs text-gray-500 font-mono">{item.quantity} × {money(item.unit_cost)}</div>
                <div className="font-bold text-gray-800 font-mono w-24 text-end">{money(item.line_total)}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-4">
            <div className="text-sm">
              <span className="text-gray-400 font-bold uppercase text-xs me-2">{t("total_label", "Total")}</span>
              <span className="text-gray-800 font-black font-mono text-lg">{money(order.total_amount)}</span>
            </div>
          </div>
        </div>

        {order.status === "received" && order.received_by && (
          <p className="text-xs text-gray-400 italic">
            {t("detail_received_line", "Received by {user} on {date}")
              .replace("{user}", order.received_by.username)
              .replace("{date}", new Date(order.received_at).toLocaleString())}
          </p>
        )}
        {order.status === "cancelled" && order.cancelled_by && (
          <p className="text-xs text-gray-400 italic">
            {t("detail_cancelled_line", "Cancelled by {user} on {date}")
              .replace("{user}", order.cancelled_by.username)
              .replace("{date}", new Date(order.cancelled_at).toLocaleString())}
          </p>
        )}

        {order.status === "pending" && (
          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button variant="primary" icon={ICONS.PurchaseOrders} onClick={() => setReceiveModalOpen(true)} className="flex-grow shadow-lg shadow-blue-500/10">
              {t("btn_receive", "Receive Stock")}
            </Button>
            <Button variant="danger" onClick={() => setCancelModalOpen(true)}>{t("btn_cancel_order", "Cancel Order")}</Button>
          </div>
        )}
        {order.status !== "pending" && (
          <div className="pt-6 border-t border-gray-50">
            <Button variant="secondary" onClick={() => navigate("/purchase-orders")}>{tCommon("btn_back", "Back")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
